import { useEffect, useState } from 'react';
import { catchError, EMPTY, tap } from 'rxjs';

import useSecuredApiV2 from './use-secured-api-v2';
import { type ResponseDto } from '../dto/ResponseDto';
import {
  ARCHIVED_CUSTOMER_ORDER_END_POINT_URL,
  CUSTOMER_ORDER_END_POINT_URL,
} from '../utils/endpoints';
import type ProblemDetailsDto from '../dto/error/problem-details-dto';

/** A site an order is for, with only what checks on the order's sites need */
export interface OrderSiteDto {
  id: number;
  name: string;
}

export interface OrderSitesResult {
  data: OrderSiteDto[];
  isLoading: boolean;
  errorMessage?: string;
}

/**
 * Loads the sites an order is for: the sites of an active order's latest revision from the customer
 * order controller, or those an archived order was for from the archived customer order controller.
 * Nothing is loaded while "orderId" is undefined.
 */
const useOrderSites = (orderId: string | undefined, isArchived: boolean): OrderSitesResult => {
  const api = useSecuredApiV2();
  const [state, setState] = useState<OrderSitesResult>({ data: [], isLoading: false });

  useEffect(() => {
    if (!orderId) {
      setState({ data: [], isLoading: false });
      return;
    }
    setState({ data: [], isLoading: true });

    const url = isArchived
      ? `${ARCHIVED_CUSTOMER_ORDER_END_POINT_URL}/${orderId}/sites`
      : `${CUSTOMER_ORDER_END_POINT_URL}/${orderId}/sites`;

    const subscription = api
      .get(url, {}, {})
      .pipe(
        tap((response: ResponseDto<OrderSiteDto[]>) => {
          setState({ data: response.data, isLoading: false });
        }),
        catchError((error: ProblemDetailsDto) => {
          setState({
            data: [],
            isLoading: false,
            errorMessage: error.detail ?? 'Failed to load the sites of this order',
          });
          return EMPTY;
        })
      )
      .subscribe();

    // Ignore the response if the order changes or the component unmounts before it arrives
    return () => {
      subscription.unsubscribe();
    };
  }, [orderId, isArchived]);

  return state;
};

export default useOrderSites;
